import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { clsx } from 'clsx';
import dayjs from 'dayjs';

import { isHighRiskArticle } from '../utils/monitoringResultsUtils';

import type IMonitoringArticleCard from './interfaces/IMonitoringArticleCard';

import keywordStyles from './sass/keywordStyles.module.scss';
import styles from './sass/MonitoringArticleCard.module.scss';

function MonitoringArticleCard({
  article,
  defaultExpanded = isHighRiskArticle(article),
}: IMonitoringArticleCard): React.JSX.Element {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

  const formattedDate = dayjs(article.date).isValid()
    ? dayjs(article.date).format('MMM D, YYYY')
    : article.date;

  return (
    <article
      className={clsx(styles.container, {
        [styles.riskCritical]: article.risk_severity === 'critical',
        [styles.riskHigh]: article.risk_severity === 'high',
        [styles.riskMedium]: article.risk_severity === 'medium',
        [styles.riskLow]: article.risk_severity === 'low',
      })}
    >
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded((prevExpanded) => !prevExpanded)}
        aria-expanded={isExpanded}
      >
        <div className={styles.headerMain}>
          {article.url ? (
            <a
              href={article.url}
              className={styles.titleLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
            >
              {article.title}
            </a>
          ) : (
            <h3 className={styles.title}>{article.title}</h3>
          )}

          <div className={styles.metaRow}>
            <span>{formattedDate}</span>
            <span aria-hidden="true">·</span>
            <span>{article.source}</span>
          </div>

          <div className={styles.badgeRow}>
            <span className={clsx(styles.badge, styles[`sentiment_${article.sentiment}`])}>
              {t(`valueChainMap.monitoring.results.sentiment.${article.sentiment}`)}
            </span>
            <span className={clsx(styles.badge, styles[`risk_${article.risk_severity}`])}>
              {t(`valueChainMap.monitoring.results.riskSeverity.${article.risk_severity}`)}
            </span>
            {typeof article.confidence === 'number' ? (
              <span className={styles.confidence}>
                {t('valueChainMap.monitoring.results.confidence', {
                  value: Math.round(article.confidence * 100),
                })}
              </span>
            ) : null}
          </div>
        </div>

        <ChevronIcon
          className={clsx(styles.chevron, { [styles.chevronExpanded]: isExpanded })}
          aria-hidden="true"
        />
      </button>

      {isExpanded ? (
        <div className={styles.body}>
          {article.summary ? (
            <p className={styles.summary}>{article.summary}</p>
          ) : null}
          {article.excerpt ? (
            <blockquote className={styles.excerpt}>{article.excerpt}</blockquote>
          ) : null}
          {article.keywords_found?.length ? (
            <div className={keywordStyles.keywords}>
              <span className={keywordStyles.keywordsLabel}>
                {t('valueChainMap.monitoring.results.keywords')}
              </span>
              <div className={keywordStyles.keywordList}>
                {article.keywords_found.map((keyword) => (
                  <span key={`${article.title}-${keyword}`} className={keywordStyles.keywordChip}>
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default MonitoringArticleCard;
